import './UfCard.css';
import type { ComponentChildren } from 'preact';
import { useEffect, useId } from 'preact/hooks';
import { CandidateRow } from '../../components/CandidateRow';
import { Chip } from '../../components/Chip';
import { detectPlatform } from '../../components/Kbd';
import { KeyValueRow } from '../../components/KeyValueRow';
import { PartyTag } from '../../components/PartyTag';
import { Portrait } from '../../components/Portrait';
import { SectionLabel } from '../../components/SectionLabel';
import { StateSeal } from '../../components/StateSeal';
import type { StatusChipProps } from '../../components/StatusChip';
import type { Baseline2022, FirstRoundPerson, FirstRoundResults, Geography } from '../../contracts/static';
import { groupGender, t } from '../../copy/strings';
import { fmtDelta, fmtPct, fmtSections, fmtVoters, MINUS } from '../../format/numbers';
import { placeName, ufIn } from '../../format/places';
import { type Ref2022, ref2022Of } from '../../model/ref2022';
import type { Candidate, LayoutMode, PlaceView, RouteOffice, UfCode } from '../../model/types';
import { openProfile, stateDetail } from '../../state/actions';
import {
  useConfig,
  useHeadline,
  useLayout,
  usePhase,
  useRouteState,
  useStatics,
  useTimeline,
  useUi,
  useView,
} from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';
import type { FirstRoundEntry } from '../hero/FirstRoundRef';
import { Duel, type DuelRace } from './Duel';
import { PlaceCard } from './PlaceCard';
import { PlaceHeader } from './PlaceHeader';
import { UfNavButtons } from './UfNavButtons';

// ── Regras puras (§8.2) ──────────────────────────────────────────────────────────────────────────────────────────

/** Seções do cartão: Presidente, Governador (só UF com 2º turno), "Eleitos no 1º turno" e indicadores. */
export type UfSection = 'president' | 'governor' | 'firstRound' | 'kpis';

/**
 * Ordem das seções (§8.2, tabela "Ordem das seções"): na aba Presidente, Presidente primeiro; na aba Governadores, a
 * disputa de governador (UF com 2º turno) ou "Eleitos no 1º turno" (UF sem) primeiro. Na TV, só a seção do cargo da
 * cena. Senado e Deputados (arquivo, P2) seguem a ordem de Presidente.
 */
export function ufSections(office: RouteOffice, hasRace: boolean, tv: boolean): UfSection[] {
  const governor = office === 'governor';
  if (tv) return [governor ? (hasRace ? 'governor' : 'firstRound') : 'president'];
  if (!governor) return hasRace ? ['president', 'governor', 'firstRound', 'kpis'] : ['president', 'firstRound', 'kpis'];
  return hasRace ? ['governor', 'president', 'firstRound', 'kpis'] : ['firstRound', 'president', 'kpis'];
}

/** Estado de uma seção de contagem (§8.2 bloco 4): antes das 17h00, sem seção totalizada, ou com votos. */
export type UfVotesState = 'before_open' | 'no_sections' | 'votes';

export function ufVotesState(
  beforeOpen: boolean,
  p: Pick<PlaceView, 'validVotes' | 'sectionsFraction'> | null | undefined,
): UfVotesState {
  if (beforeOpen) return 'before_open';
  return p && (p.validVotes > 0 || p.sectionsFraction > 0) ? 'votes' : 'no_sections';
}

/** Dica do rótulo "Presidente {uf_em}" (§8.2, X8 de §15.1.4). */
export type UfHintKind = 'mouse' | 'touch' | 'keyboard';

export interface UfHintInput {
  office: RouteOffice;
  /** Malha municipal da UF desenhada (§9.2): sem ela, não há município para clicar (AC-8.2-07). */
  municipalMap: boolean;
  tv: boolean;
  /** Modalidade de teclado (`ui.keyboard`, §15.2.1). */
  keyboard: boolean;
  /** Ponteiro grosso ou layout de celular (§8.8: "Toque num município"). */
  coarse: boolean;
}

/** Só na aba Presidente e com a malha municipal da UF; teclado → busca (Ctrl K); toque → "Toque"; senão "Clique". */
export function ufHintKind(o: UfHintInput): UfHintKind | null {
  if (o.office !== 'president' || !o.municipalMap || o.tv) return null;
  if (o.keyboard) return 'keyboard';
  return o.coarse ? 'touch' : 'mouse';
}

const PHONE_MODES: ReadonlySet<LayoutMode> = new Set(['flow', 'tablet', 'land']);

/** Eleitorado da UF no subtítulo: o de referência da `Geography` (§8.2 bloco 3); sem ela, o que a vista permite. */
export function ufElectorate(
  geo: Geography | null | undefined,
  uf: UfCode,
  place: Pick<PlaceView, 'remainingElectorate' | 'electorateFraction'> | null | undefined,
): number | null {
  const ref = geo?.places.find((p) => p.code === uf)?.electorate_ref;
  if (typeof ref === 'number' && Number.isFinite(ref) && ref > 0) return ref;
  if (place && place.electorateFraction < 1 && place.remainingElectorate > 0) {
    return Math.round(place.remainingElectorate / (1 - place.electorateFraction));
  }
  return null;
}

export type { Ref2022Item } from '../../model/ref2022';

/** Linhas "2022" e "Variação" da seção Presidente (§8.2). */
export interface UfRef2022 {
  items: Ref2022['items'];
  /**
   * Variação por PARTIDO (simetria, AC-8.2-04), na ordem do par de 2026 [A, B]: delta = fatia de 2026 − fatia do mesmo
   * partido em 2022, em fração. null quando um partido do par não disputou o 2º turno de 2022.
   */
  variation: { sigla: [string, string]; delta: [number, number]; base: [number, number] } | null;
}

const siglaOf = (c: Pick<Candidate, 'party_code'>): string => partyOf(c).display || c.party_code;

/** "2022" e "Variação" da UF (a conta é a de `model/ref2022.ts`, a mesma do `MapTooltip`). */
export function ufRef2022(
  baseline: Baseline2022 | null | undefined,
  uf: UfCode,
  pair: readonly [Candidate, Candidate],
  shares: readonly [number, number],
): UfRef2022 | null {
  const r = ref2022Of(baseline, baseline?.places[uf], pair, shares);
  if (!r) return null;
  const v = r.variation;
  return {
    items: r.items,
    variation: v ? { sigla: [siglaOf(pair[0]), siglaOf(pair[1])], delta: v.delta, base: v.base } : null,
  };
}

/** `FirstRoundRef compact` do duelo: fatias do 1º turno dos dois do 2º turno (`governor[uf].runoff`), pelo número. */
export function governorFirstRound(
  fr: FirstRoundResults | null | undefined,
  uf: UfCode,
  pair: readonly [Candidate, Candidate],
): [FirstRoundEntry, FirstRoundEntry] | null {
  const runoff = fr?.governor[uf]?.runoff;
  if (!runoff) return null;
  const a = runoff.find((p) => p.number === pair[0].number);
  const b = runoff.find((p) => p.number === pair[1].number);
  if (!a || !b || !Number.isFinite(a.share) || !Number.isFinite(b.share)) return null;
  return [
    { candidate: pair[0], share: a.share },
    { candidate: pair[1], share: b.share },
  ];
}

/** Chip da seção Governador (§8.1 tabela de chips, §8.2 bloco 4): antes das 17h00 e sem dado, "Aguardando". */
export function governorChip(
  race: (DuelRace & Partial<Pick<PlaceView, 'sectionsFraction'>>) | null,
  pre: boolean,
): StatusChipProps {
  if (pre || !race) return { status: 'no_data', sectionsFraction: 0, decision: null, gender: null };
  const decision = race.tseResult ?? race.mathDecision;
  const winner = decision ? race.candidates.find((c) => c.number === decision.winner) : undefined;
  const gender = winner?.gender ?? null;
  return {
    status: race.status,
    sectionsFraction: race.sectionsFraction ?? 0,
    decision,
    gender,
    label: decision?.kind === 'tse' ? t('chip.tse', { 'X.gender': gender }) : decision ? t('chip.math') : undefined,
  };
}

/** "+3,9" → "mais 3,9"; "−3,9" → "menos 3,9" (§15.4.3 regra 4); "0,0" fica. */
function deltaSr(text: string): string {
  if (text.startsWith('+')) return t('compare.delta.more', { d: text.slice(1) });
  if (text.startsWith(MINUS)) return t('compare.delta.less', { d: text.slice(MINUS.length) });
  return text;
}

const pct1 = (v: number): string => fmtPct(v, 1);

/** Bandeira da UF (§2.7.9): nome fixo `/flags/{uf}.webp`, cache de 7 dias. */
const flagSrc = (uf: UfCode): string => `${import.meta.env.BASE_URL}flags/${uf.toLowerCase()}.webp`;

// ── Componente ───────────────────────────────────────────────────────────────────────────────────────────────────

export interface UfCardProps {
  /**
   * A malha municipal da UF está desenhada (§9.2; Fase 10): liga a dica "Clique num município" (AC-8.2-07). O slot do
   * App a lê do mapa (features/places não importa features/map, FE §4.6); padrão false.
   */
  municipalMap?: boolean;
  /** Chance de governador da UF (C03, K06), logo abaixo do duelo; quem preenche (app/) decide se ela aparece. */
  governorChance?: (uf: UfCode) => ComponentChildren;
}

/**
 * Cartão da UF (§8.2): Presidente na UF com "2022" e "Variação", duelo de governador nas UFs com 2º turno, "Eleitos no 1º
 * turno" e indicadores, na ordem da aba (§8.2); ‹ › × no canto. Lê a rota (`place.kind === 'uf'`), a vista exibida (ao
 * vivo ou replay) e os estáticos; nada aqui busca dado (§2.2.2): `state/{uf}.json` é pedido pela ação `stateDetail`
 * ao abrir a UF e a cada `seq` ao vivo (etapa 5 de §15.7.4; o `FeedClient` guarda em cache para o tooltip e o
 * município). Neutralidade: linhas de candidato e nomes na ordem do contrato (13 | 22), duelo com número menor à
 * esquerda, mesmas classes para os dois lados.
 */
export function UfCard({ municipalMap = false, governorChance }: UfCardProps) {
  const route = useRouteState();
  const view = useView();
  const statics = useStatics();
  const config = useConfig();
  const layout = useLayout();
  const ui = useUi();
  const phase = usePhase();
  const headline = useHeadline();
  const { live } = useTimeline();
  const id = useId();

  const uf = route.place.kind === 'uf' ? route.place.uf : null;
  const tv = layout.mode === 'tv';
  const seq = view?.seq ?? null;

  // Etapa 5 de §15.7.4: o detalhe da UF sob demanda, ao abrir e a cada seq ao vivo (o FeedClient deduplica e guarda).
  useEffect(() => {
    if (uf === null || !live || tv || seq === null) return;
    stateDetail(uf, 'president').catch(() => null);
  }, [uf, seq, live, tv]);

  if (uf === null) return null;

  const photos = config.features.photos;
  const office = route.office;
  const beforeOpen = phase === 'pre_open' || headline === 'countdown';
  const interactive = !tv;

  // ── dados ──
  const place: PlaceView | null = view?.places[uf] ?? null;
  const president = view?.candidates ?? statics.candidates?.president ?? null;
  const governorList = statics.candidates?.governor?.[uf] ?? null;
  const raceView = view?.governor[uf] ?? null;
  const hasRace = config.features.governors && (view ? raceView !== null : governorList !== null);
  // sem snapshot, a disputa nasce do cadastro (nomes e "Aguardando", §8.3 PONTO DE EXTENSÃO)
  const stub: DuelRace | null = governorList
    ? {
        candidates: governorList,
        shares: [0, 0],
        votes: [0, 0],
        validVotes: 0,
        remainingElectorate: 0,
        status: 'no_data',
        mathDecision: null,
        tseResult: null,
      }
    : null;
  const race: (DuelRace & { sectionsFraction?: number }) | null = hasRace ? (raceView ?? stub) : null;

  const presState = ufVotesState(beforeOpen, place);
  const ref2022 = place && president ? ufRef2022(statics.baseline, uf, president, place.shares) : null;

  const fr = statics.firstRound ?? null;
  const frGovernor = !hasRace && fr?.governor[uf]?.status === 'elected' ? (fr.governor[uf]?.elected ?? null) : null;
  const frSenate = fr?.senate[uf]?.elected ?? null;

  const kpiSource: Pick<PlaceView, 'validVotes' | 'sectionsFraction' | 'turnoutRate' | 'blankNullRate'> | null =
    office === 'governor' && hasRace ? raceView : place;
  const kpiOn = ufVotesState(beforeOpen, kpiSource) === 'votes' && kpiSource !== null;

  const coarse = layout.pointerCoarse || PHONE_MODES.has(layout.mode);
  const hint = ufHintKind({ office, municipalMap, tv, keyboard: ui.keyboard, coarse });
  const kbd = detectPlatform() === 'mac' ? t('kbd.search.mac') : t('kbd.search.other');
  const hintText =
    hint === 'keyboard'
      ? t('uf.hint.keyboard', { KBD: kbd })
      : hint === 'touch'
        ? t('uf.hint.touch')
        : hint === 'mouse'
          ? t('uf.hint.mouse')
          : null;

  // Nome pela tabela de §13.4.4 (format/places, a mesma fonte de `PlaceInfo.name` e do artigo de "Presidente {uf_em}"):
  // existe antes de a Geography chegar (etapa 3), então o título nunca pisca nem muda de fonte.
  const name = placeName(uf);
  const electorate = ufElectorate(statics.geography, uf, place);
  const sub =
    electorate === null
      ? null
      : t('place.sub', { S: fmtSections(place?.sectionsFraction ?? 0), E: fmtVoters(electorate) });

  const describe = (c: Pick<Candidate, 'display_name' | 'party_code' | 'number'>) =>
    t('hero.leader.aria', { NOME: c.display_name, SIGLA: siglaOf(c), NUM: c.number });

  /** Nomes antes dos votos (retrato 26, nome, PartyTag), na ordem de número; abrem o perfil (§8.6). */
  const names = (list: readonly Candidate[], profileOffice: 'president' | 'governor', key: string) => (
    <ul class="uf-card__names">
      {list.map((c, i) => {
        const body = (
          <>
            <Portrait candidate={c} photo={photos ? c.photo : null} size={26} />
            <span class="uf-card__names-name">{c.display_name}</span>
            <PartyTag candidate={c} size="sm" />
          </>
        );
        const descId = `${id}-${key}${i}`;
        return (
          <li key={c.number} class={`uf-card__names-item ${partyClass(partyOf(c))}`} data-number={c.number}>
            {interactive ? (
              <button
                type="button"
                class="uf-card__names-cell"
                aria-describedby={descId}
                onClick={() => openProfile(c.number, profileOffice, 'list')}
              >
                {body}
                <span id={descId} hidden>
                  {describe(c)}
                </span>
              </button>
            ) : (
              <span class="uf-card__names-cell">{body}</span>
            )}
          </li>
        );
      })}
    </ul>
  );

  // ── seções ──
  const otherClass = (s: UfSection) =>
    (office === 'governor' && s === 'president') || (office !== 'governor' && s === 'governor')
      ? ' uf-card__other'
      : '';

  const presidentSection = () => (
    <section
      key="president"
      class={`place-card__section uf-card__president${otherClass('president')}`}
      data-testid="uf-card-president"
      data-state={presState}
      aria-labelledby={`${id}-president`}
    >
      <SectionLabel
        id={`${id}-president`}
        label={t('uf.section.president', { uf_em: ufIn(uf) })}
        actions={
          hintText ? (
            <p class="uf-card__hint" data-testid="uf-card-hint" data-hint={hint ?? undefined} aria-hidden="true">
              {hintText}
            </p>
          ) : undefined
        }
      />
      {presState !== 'votes' || place === null ? (
        <>
          <p class="place-note">{t(presState === 'before_open' ? 'common.votesFrom17' : 'uf.noSections')}</p>
          {president ? names(president, 'president', 'p') : null}
        </>
      ) : (
        <>
          <div class="uf-card__rows">
            {(president ?? []).map((c, i) => (
              <CandidateRow
                key={c.number}
                candidate={c}
                photo={photos ? c.photo : null}
                share={place.shares[i as 0 | 1]}
                portraitSize={30}
                early={place.sectionsFraction < 0.01}
                onOpen={interactive ? () => openProfile(c.number, 'president', 'list') : undefined}
              />
            ))}
          </div>
          {ref2022 && !tv ? refLines(ref2022) : null}
        </>
      )}
    </section>
  );

  const governorSection = () =>
    race ? (
      <section
        key="governor"
        class={`place-card__section uf-card__governor${otherClass('governor')}`}
        data-testid="uf-card-governor"
        aria-labelledby={`${id}-governor`}
      >
        <SectionLabel id={`${id}-governor`} label={t('uf.section.governor')} status={governorChip(race, beforeOpen)} />
        <Duel race={race} firstRound={governorFirstRound(fr, uf, race.candidates)} pre={beforeOpen} />
        {governorChance ? governorChance(uf) : null}
      </section>
    ) : null;

  const firstRoundSection = () => {
    if (!frGovernor && !frSenate) return null;
    const people: FirstRoundPerson[] = [...(frGovernor ? [frGovernor] : []), ...(frSenate ?? [])];
    return (
      <section
        key="firstRound"
        class="place-card__section uf-card__first-round"
        data-testid="uf-card-first-round"
        aria-labelledby={`${id}-first-round`}
      >
        <SectionLabel
          id={`${id}-first-round`}
          label={t('uf.firstRound.title', { 'G.gender': groupGender(people.map((p) => p.gender)) })}
        />
        {frGovernor ? (
          <div class="uf-card__fr-block uf-card__fr-governor" data-testid="uf-card-fr-governor">
            <SectionLabel
              level={4}
              label={t('uf.firstRound.governor')}
              actions={<Chip tone="done" label={t('chip.electedFirstRound', { 'X.gender': frGovernor.gender })} />}
            />
            {frRow(frGovernor)}
          </div>
        ) : null}
        {frSenate ? (
          <div class="uf-card__fr-block uf-card__fr-senate" data-testid="uf-card-fr-senate">
            <SectionLabel
              level={4}
              label={t('uf.firstRound.senate')}
              actions={
                <Chip
                  tone="done"
                  label={t('chip.electedFirstRoundGroup', { 'G.gender': groupGender(frSenate.map((p) => p.gender)) })}
                />
              }
            />
            {frSenate.map((p) => frRow(p))}
          </div>
        ) : null}
      </section>
    );
  };

  /** Linha compacta de eleito no 1º turno (retrato 28, nome, PartyTag, % do 1º turno com 1 casa); só leitura. */
  const frRow = (p: FirstRoundPerson) => (
    <CandidateRow key={p.number} candidate={p} photo={photos ? p.photo : null} share={p.share} portraitSize={28} />
  );

  const kpisSection = () => (
    <div key="kpis" class="place-card__section uf-card__kpis" data-testid="uf-card-kpis">
      <dl class="place-kpis">
        <KeyValueRow label={t('kpi.turnout')} value={kpiOn ? (kpiSource?.turnoutRate ?? null) : null} format={pct1} />
        <KeyValueRow
          label={t('kpi.blankNull')}
          value={kpiOn ? (kpiSource?.blankNullRate ?? null) : null}
          format={pct1}
        />
      </dl>
    </div>
  );

  const render: Record<UfSection, () => ComponentChildren> = {
    president: presidentSection,
    governor: governorSection,
    firstRound: firstRoundSection,
    kpis: kpisSection,
  };

  return (
    <PlaceCard kind="uf" class="uf-card" trimRoot={layout.mode === 'desktop'} state={presState}>
      <PlaceHeader
        title={name}
        sub={sub}
        seal={<StateSeal uf={uf} size={40} src={flagSrc(uf)} />}
        buttons={tv ? 0 : 3}
      />
      <UfNavButtons kind="uf" />
      {/* CASCA: Fase 11 — na TV, o kicker da cena (§12.2) entra no lugar do UpLink (TvMode). */}
      {ufSections(office, race !== null, tv).map((s) => render[s]())}
    </PlaceCard>
  );
}

/** Linhas "2022" (ordem de número) e "Variação" (pela sigla dos dois partidos, AC-8.2-04) da seção Presidente. */
function refLines(r: UfRef2022) {
  const v = r.variation;
  const dA = v ? fmtDelta(v.delta[0]) : '';
  const dB = v ? fmtDelta(v.delta[1]) : '';
  return (
    <>
      <p class="place-ref uf-card__ref-2022" data-testid="uf-card-2022" title={t('compare.2022.title')}>
        <span class="place-ref__label">{t('compare.2022.label')}</span>
        {r.items.map((it, i) => (
          <span key={it.number} class={`place-ref__item ${partyClass(partyOf(it.party_code))}`} data-number={it.number}>
            {i > 0 ? <span class="sr-only">, </span> : null}
            <span class="place-ref__swatch" data-dataviz="" aria-hidden="true" />
            {t('compare.2022.item', { NOME2022: it.name, P: pct1(it.share) })}
          </span>
        ))}
      </p>
      {v ? (
        <p
          class="place-ref uf-card__ref-variation"
          data-testid="uf-card-variation"
          title={t('compare.variation.title', {
            SIGLA_A: v.sigla[0],
            P1: pct1(v.base[0]),
            SIGLA_B: v.sigla[1],
            P2: pct1(v.base[1]),
          })}
        >
          <span class="place-ref__label">{t('compare.variation.label')}</span>
          <span aria-hidden="true">
            {t('compare.variation.value', { SIGLA_A: v.sigla[0], dA, SIGLA_B: v.sigla[1], dB })}
          </span>
          <span class="sr-only">
            {t('compare.variation.sr', { SIGLA_A: v.sigla[0], dA: deltaSr(dA), SIGLA_B: v.sigla[1], dB: deltaSr(dB) })}
          </span>
        </p>
      ) : null}
    </>
  );
}
